import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Safari (and post-morph) variant picker reliability.
 * - Ensures label clicks always select the radio and emit change
 * - Keeps checked + data-current-checked in sync for visible selected styles
 * - Updates legend with current value (Size · 12 oz)
 */

/** @param {HTMLInputElement} input */
function dispatchVariantChange(input) {
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/** @param {HTMLInputElement} input */
function applyFieldsetSelection(input) {
  const fieldset = input.closest('fieldset');
  if (!fieldset) {
    input.checked = true;
    input.dataset.currentChecked = 'true';
    return;
  }

  fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const isSelected = radio === input;
    radio.checked = isSelected;
    radio.dataset.currentChecked = isSelected ? 'true' : 'false';
    if (!isSelected) {
      radio.dataset.previousChecked = 'false';
    }
  });
}

/** @param {Element} picker */
function updateLegends(picker) {
  picker.querySelectorAll('fieldset').forEach((fieldset) => {
    const valueEl = fieldset.querySelector('.wc-variant-legend-value, .variant-option__swatch-value');
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (valueEl && checked instanceof HTMLInputElement) {
      valueEl.textContent = checked.value;
    }
  });
}

/** @param {Element} picker */
function reconcilePickerState(picker) {
  picker.querySelectorAll('fieldset').forEach((fieldset) => {
    let checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) {
      checked = fieldset.querySelector('input[type="radio"][data-current-checked="true"]');
    }
    if (checked instanceof HTMLInputElement) {
      applyFieldsetSelection(checked);
    }
  });
  updateLegends(picker);
}

function initPickers() {
  document.querySelectorAll('variant-picker').forEach(reconcilePickerState);
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label');
    if (!label) return;

    const picker = label.closest('variant-picker');
    if (!picker) return;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;
    if (input.disabled || input.getAttribute('aria-disabled') === 'true') return;

    const wasChecked = input.checked;

    window.setTimeout(() => {
      applyFieldsetSelection(input);

      if (!wasChecked) {
        dispatchVariantChange(input);
      }

      updateLegends(picker);
    }, 0);
  },
  true
);

/** @param {VariantUpdateEvent} event */
function syncPickerFromVariant(event) {
  const variant = event.detail.resource;
  if (!variant) return;

  const sourcePicker = event.target;
  if (!(sourcePicker instanceof HTMLElement) || sourcePicker.tagName !== 'VARIANT-PICKER') return;

  const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);

  sourcePicker.querySelectorAll('fieldset').forEach((fieldset, index) => {
    const optionValue = options[index];
    if (!optionValue) return;

    fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const isSelected = radio.value === optionValue;
      radio.checked = isSelected;
      radio.dataset.currentChecked = isSelected ? 'true' : 'false';
      if (!isSelected) {
        radio.dataset.previousChecked = 'false';
      }
    });
  });

  updateLegends(sourcePicker);
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  if (!(event instanceof VariantUpdateEvent)) return;
  syncPickerFromVariant(event);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPickers);
} else {
  initPickers();
}